import { Popover, PopoverContent, PopoverTrigger, useDisclosure } from '@nextui-org/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import { NewGearIcon, NewIconLocalRepository } from '~/components/icons'
import { Link } from '~/components/common/Link'
import { ConfirmationModal } from '~/components/modals/ConfirmationModal'
import { CloseIcon, Modal, ModalBody } from '~/components/modals/CurieModal'
import { ConnectPopoverContent } from './components/ConnectPopoverContent'
import { SettingItem } from '~/components/common/SettingItem'
import { Tag } from '~/components/common/Tag'
import { Connections, ConnectionType, TypeConnection } from '~/constants/connections'
import { DeleteWorkspaceIcon } from '~/constants/icon'
import { bridge } from '~/services/bridge'
import { fetchWrapper } from '~/utils/fetchWrapper'
import { BetterToast } from '~/utils/toast'
import { cm } from '~/utils/utils'

/**
 * Hit the OAuth login endpoint in preflight mode to check whether the
 * current plan / workspace membership permits the integration BEFORE
 * opening the popup. The backend runs every access check (feature-access,
 * workspace membership, billing, adapter existence) and returns
 * `{ok: true}` on success or a 4xx on failure.
 *
 * On 4xx we rely on fetchWrapper's existing error handling:
 *  - 403 with detail={error: 'upgrade_required', message} → ModalContext
 *    intercepts and opens the upgrade-plan modal (the UX flex/pro users
 *    actually need when hitting the version-control feature gate).
 *  - 403 with plain string detail → ModalContext toasts the message.
 *  - Other 4xx → handleGenericError toasts the message.
 *
 * Returns true if OAuth is permitted (caller opens the popup), false
 * otherwise (the user-facing error UX has already happened upstream).
 */
async function preflightOAuthConnect(
  connectionId: string,
  workspaceUuid: string
): Promise<boolean> {
  const url = `/oauth/${connectionId.toLowerCase()}/login?workspace_uuid=${workspaceUuid}&preflight=1`
  try {
    const res = await fetchWrapper(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
    return res.ok
  } catch {
    return false
  }
}
import AuthorizeBranchForm from './components/AuthorizeBranchForm'
import { CustomIntegrationForm } from './components/CustomIntegrationForm'

// Import extracted components and hooks
import { useBillingContext } from '~/contexts/BillingContext'
import { useConnectionManagementContext } from '~/contexts/ConnectionManagementContext'
import { FlatIndex, useIndexesContext } from '~/contexts/IndexesContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useOAuthPopup } from '~/hooks/useOAuthPopup'
import { useRuntimeSettings } from '~/hooks/useRuntimeSettings'
import { ConnectedReposTable } from './components/ConnectedReposTable'
import { LocalRepositorySection } from './components/LocalRepositorySection'
import { RuntimeSettingsDrawer } from './components/RuntimeSettingsDrawer'
import { ConnectionUnion, PageView } from './components/types'

// Window extension for directory picker
declare global {
  interface Window {
    showDirectoryPicker: (options: {
      mode: 'read' | 'readwrite'
      startIn: 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos'
    }) => Promise<FileSystemDirectoryHandle>
  }
}

const ModalCloseButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className='absolute top-5 right-5 rounded-full text-neutral-icon-default hover:bg-neutral-fill-fill-4 hover:text-neutral-icon-hover transition-colors select-none'
    aria-label='Close modal'
  >
    <CloseIcon className='size-6' />
  </button>
)

interface LocalRepositoryCardProps {
  onSelect: () => void
}

interface RepositoryConnectionCardProps {
  connection: TypeConnection
  isConnected: boolean
  isUpdatingToken: boolean
  repositoryConnectionState: boolean
  onSelect: () => void
  onTogglePopover: () => void
  onOAuthSuccess: (connectionId: string) => void
}

interface SelfHostedConnectionCardProps {
  onSelect: () => void
}

// Local Repository Card Component
function LocalRepositoryCard({ onSelect }: LocalRepositoryCardProps) {
  return (
    <div
      className='cursor-pointer w-full min-w-0 max-w-[400px] sm:min-w-[320px]'
      onMouseDown={() => {}}
      onMouseUp={() => {}}
      onMouseLeave={() => {}}
      aria-label='Local Repository Card'
    >
      <SettingItem
        variant='card'
        isConnected={false}
        className=''
        title={
          <>
            <div className='w-8 h-8 flex items-center justify-center'>
              <NewIconLocalRepository className='w-8 h-8 text-gray-600' />
            </div>
            <span className='text-[15px] font-semibold text-[rgba(46,31,54,0.90)] leading-normal font-mono select-none'>
              Local Repo
            </span>
          </>
        }
        onClick={onSelect}
      >
        <div className='p-2 rounded-lg pointer-events-none'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
          >
            <g clipPath='url(#clip0_203_17389)'>
              <path
                d='M11.3537 8.52035L6.35372 13.5203C6.30727 13.5668 6.25212 13.6037 6.19142 13.6288C6.13072 13.6539 6.06567 13.6669 5.99997 13.6669C5.93428 13.6669 5.86922 13.6539 5.80852 13.6288C5.74783 13.6037 5.69268 13.5668 5.64622 13.5203C5.59977 13.4739 5.56292 13.4187 5.53778 13.358C5.51263 13.2974 5.49969 13.2323 5.49969 13.1666C5.49969 13.1009 5.51263 13.0358 5.53778 12.9752C5.56292 12.9145 5.59977 12.8593 5.64622 12.8128L10.2931 8.1666L5.64622 3.52035C5.5524 3.42653 5.49969 3.29928 5.49969 3.1666C5.49969 3.03392 5.5524 2.90667 5.64622 2.81285C5.74004 2.71903 5.86729 2.66632 5.99997 2.66632C6.13265 2.66632 6.2599 2.71903 6.35372 2.81285L11.3537 7.81285C11.4002 7.85928 11.4371 7.91443 11.4623 7.97513C11.4874 8.03583 11.5004 8.10089 11.5004 8.1666C11.5004 8.23231 11.4874 8.29737 11.4623 8.35807C11.4371 8.41877 11.4002 8.47391 11.3537 8.52035Z'
                fill='black'
                fillOpacity='0.88'
              />
            </g>
            <defs>
              <clipPath id='clip0_203_17389'>
                <rect width='16' height='16' fill='white' transform='translate(0 0.166626)' />
              </clipPath>
            </defs>
          </svg>
        </div>
      </SettingItem>
    </div>
  )
}

// Repository Connection Card Component
function RepositoryConnectionCard({
  connection,
  isConnected,
  isUpdatingToken,
  repositoryConnectionState,
  onSelect,
  onTogglePopover,
  onOAuthSuccess
}: RepositoryConnectionCardProps) {
  const [pressedCardId, setPressedCardId] = useState<string | null>(null)
  const { billingInfo } = useBillingContext()
  const { currentWorkspace } = useWorkspaceContext()
  const { openOAuthPopup } = useOAuthPopup()

  // Synchronous so window.open (inside openOAuthPopup) stays in the click's user
  // activation and isn't popup-blocked. The plan/access preflight runs as
  // beforeOpen — AFTER the popup is opened. A flex/pro denial returns 403 with
  // detail={error: 'upgrade_required', message}, which fetchWrapper routes
  // through ModalContext → upgrade-plan modal; other 4xx surface as toasts.
  // Either way preflight resolves false → openOAuthPopup closes the popup.
  const handleOAuthConnect = () => {
    // Use billing context instead of making additional API calls
    if (!billingInfo) {
      console.warn('Billing info not available')
      return
    }

    if (!currentWorkspace) {
      console.warn('Current workspace not available')
      return
    }

    const oauthUrl = `/oauth/${connection.id.toLowerCase()}/login?workspace_uuid=${currentWorkspace.uuid}`
    openOAuthPopup(oauthUrl, {
      beforeOpen: () => preflightOAuthConnect(connection.id, currentWorkspace.uuid),
      onSuccess: data => {
        console.log('OAuth success:', data)
        // Navigate to index modal after successful connection
        onOAuthSuccess(connection.id)
      },
      onError: error => {
        console.error('OAuth error:', error)
      }
    })
  }

  return (
    <Popover
      key={connection.id}
      placement='bottom-end'
      offset={-20}
      crossOffset={-12}
      onOpenChange={isOpen => {
        if (isOpen) {
          setPressedCardId(connection.id)
        } else {
          setPressedCardId(null)
        }
      }}
      onClose={onTogglePopover}
      isOpen={repositoryConnectionState}
      classNames={{
        content: 'shadow-popover, min-w-[223px]',
        trigger:
          'aria-expanded:!opacity-100 aria-expanded:!scale-100 w-full min-w-0 max-w-[400px] sm:min-w-[320px]'
      }}
    >
      <PopoverTrigger>
        <div className='cursor-pointer'>
          <SettingItem
            variant='card'
            isConnected={isConnected}
            className={pressedCardId === connection.id && !isConnected ? '!bg-[#EAEAEA]' : ''}
            title={
              <>
                <div className='w-8 h-8 flex items-center justify-center'>{connection.icon}</div>
                <span className='text-[15px] font-semibold text-[rgba(46,31,54,0.90)] leading-normal font-mono select-none'>
                  {connection.name}
                </span>
              </>
            }
            onClick={() => {
              // If it's connected status, switch to detail page
              if (isConnected) {
                return onSelect()
              }
              onTogglePopover()
            }}
          >
            <div
              className={`p-2 rounded-lg pointer-events-none ${isUpdatingToken ? 'opacity-50' : ''}`}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
              >
                <g clipPath='url(#clip0_203_17389)'>
                  <path
                    d='M11.3537 8.52035L6.35372 13.5203C6.30727 13.5668 6.25212 13.6037 6.19142 13.6288C6.13072 13.6539 6.06567 13.6669 5.99997 13.6669C5.93428 13.6669 5.86922 13.6539 5.80852 13.6288C5.74783 13.6037 5.69268 13.5668 5.64622 13.5203C5.59977 13.4739 5.56292 13.4187 5.53778 13.358C5.51263 13.2974 5.49969 13.2323 5.49969 13.1666C5.49969 13.1009 5.51263 13.0358 5.53778 12.9752C5.56292 12.9145 5.59977 12.8593 5.64622 12.8128L10.2931 8.1666L5.64622 3.52035C5.5524 3.42653 5.49969 3.29928 5.49969 3.1666C5.49969 3.03392 5.5524 2.90667 5.64622 2.81285C5.74004 2.71903 5.86729 2.66632 5.99997 2.66632C6.13265 2.66632 6.2599 2.71903 6.35372 2.81285L11.3537 7.81285C11.4002 7.85928 11.4371 7.91443 11.4623 7.97513C11.4874 8.03583 11.5004 8.10089 11.5004 8.1666C11.5004 8.23231 11.4874 8.29737 11.4623 8.35807C11.4371 8.41877 11.4002 8.47391 11.3537 8.52035Z'
                    fill='black'
                    fillOpacity='0.88'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_203_17389'>
                    <rect width='16' height='16' fill='white' transform='translate(0 0.166626)' />
                  </clipPath>
                </defs>
              </svg>
            </div>
          </SettingItem>
        </div>
      </PopoverTrigger>
      <PopoverContent className='!rounded-xl p-2'>
        <ConnectPopoverContent
          conn={connection}
          isUpdatingToken={{ [connection.id]: isUpdatingToken }}
          isConnected={false}
          onConnect={async () => {
            handleOAuthConnect()
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

// Self-hosted Connection Card Component
function SelfHostedConnectionCard({ onSelect }: SelfHostedConnectionCardProps) {
  return (
    <div className='cursor-pointer w-full min-w-0 max-w-[400px] sm:min-w-[320px]'>
      <SettingItem
        variant='card'
        isConnected={false}
        className=''
        title={
          <>
            <div className='w-8 h-8 flex items-center justify-center'>
              <svg
                width='32'
                height='33'
                viewBox='0 0 32 33'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <rect y='0.833374' width='32' height='32' rx='16' fill='black' fillOpacity='0.25' />
                <path
                  d='M22.4096 12.0428C22.4008 12.027 22.3885 12.0111 22.3745 11.9988C22.2866 11.9109 22.1442 11.9109 22.0563 11.9988L19.0276 15.0275L17.834 13.834L20.8645 10.8035C20.8786 10.7895 20.8891 10.7754 20.8997 10.7596C20.9629 10.6524 20.9278 10.5152 20.8206 10.452C19.0944 9.43243 16.8338 9.66622 15.3485 11.1498C14.1708 12.3275 13.7805 13.9975 14.1795 15.4986L9.02916 20.649C8.97642 20.7018 8.97994 20.7879 9.03443 20.8424L12.0174 23.8254C12.0719 23.8799 12.1598 23.8834 12.2108 23.8307L17.3594 18.682C18.8624 19.0828 20.5323 18.6943 21.7118 17.5149C23.1954 16.0295 23.4292 13.769 22.4096 12.0428ZM20.817 16.6184C19.7958 17.6397 18.2629 17.8365 17.0448 17.209L16.8901 17.3637L16.8883 17.3619L12.0719 22.1801L10.6797 20.7879L14.7245 16.7432C14.7245 16.7432 14.7245 16.7449 14.7262 16.7449L15.6544 15.8168C15.0268 14.5986 15.2237 13.0658 16.245 12.0445C16.5744 11.7149 16.971 11.4602 17.4079 11.2979C17.8447 11.1356 18.3115 11.0695 18.7762 11.1041L16.4401 13.4385C16.3346 13.5441 16.2753 13.6873 16.2753 13.8366C16.2753 13.9859 16.3346 14.1291 16.4401 14.2348L18.6286 16.4233C18.7342 16.5288 18.8774 16.588 19.0267 16.588C19.176 16.588 19.3192 16.5288 19.4249 16.4233L21.7592 14.0889C21.8243 14.9994 21.5061 15.9311 20.817 16.6184Z'
                  fill='white'
                />
              </svg>
            </div>
            <span className='text-[15px] font-semibold text-[rgba(46,31,54,0.90)] leading-normal font-mono select-none'>
              Self-hosted connection
            </span>
          </>
        }
        onClick={onSelect}
      >
        <div className='p-2 rounded-lg pointer-events-none'>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
          >
            <g clipPath='url(#clip0_203_17389)'>
              <path
                d='M11.3537 8.52035L6.35372 13.5203C6.30727 13.5668 6.25212 13.6037 6.19142 13.6288C6.13072 13.6539 6.06567 13.6669 5.99997 13.6669C5.93428 13.6669 5.86922 13.6539 5.80852 13.6288C5.74783 13.6037 5.69268 13.5668 5.64622 13.5203C5.59977 13.4739 5.56292 13.4187 5.53778 13.358C5.51263 13.2974 5.49969 13.2323 5.49969 13.1666C5.49969 13.1009 5.51263 13.0358 5.53778 12.9752C5.56292 12.9145 5.59977 12.8593 5.64622 12.8128L10.2931 8.1666L5.64622 3.52035C5.5524 3.42653 5.49969 3.29928 5.49969 3.1666C5.49969 3.03392 5.5524 2.90667 5.64622 2.81285C5.74004 2.71903 5.86729 2.66632 5.99997 2.66632C6.13265 2.66632 6.2599 2.71903 6.35372 2.81285L11.3537 7.81285C11.4002 7.85928 11.4371 7.91443 11.4623 7.97513C11.4874 8.03583 11.5004 8.10089 11.5004 8.1666C11.5004 8.23231 11.4874 8.29737 11.4623 8.35807C11.4371 8.41877 11.4002 8.47391 11.3537 8.52035Z'
                fill='black'
                fillOpacity='0.88'
              />
            </g>
            <defs>
              <clipPath id='clip0_203_17389'>
                <rect width='16' height='16' fill='white' transform='translate(0 0.166626)' />
              </clipPath>
            </defs>
          </svg>
        </div>
      </SettingItem>
    </div>
  )
}

interface IndexDetailHeaderProps {
  connection: ConnectionUnion
  navigateToCardList: () => void
  manageConnectionPopoverOpen: boolean
  onManageConnectionPopoverOpenChange: (isOpen: boolean) => void
  closeManageConnectionPopover: () => void
  connectionHook: ReturnType<typeof useConnectionManagementContext>
  onOAuthSuccess: (connectionId: string) => void
}

// Add: Top navigation bar component
const IndexDetailHeader = ({
  connection,
  navigateToCardList,
  manageConnectionPopoverOpen,
  onManageConnectionPopoverOpenChange,
  closeManageConnectionPopover,
  connectionHook,
  onOAuthSuccess
}: IndexDetailHeaderProps) => {
  const isLocalConnection = 'id' in connection && connection.id === 'local'
  const [showDeleteConnectionModal, setShowDeleteConnectionModal] = useState(false)
  const [connectionToDelete, setConnectionToDelete] = useState<string | null>(null)
  const [isDeletingConnection, setIsDeletingConnection] = useState(false)
  const { currentWorkspace } = useWorkspaceContext()
  const { openOAuthPopup } = useOAuthPopup()
  const handleDeleteClick = (connId: string) => {
    setConnectionToDelete(connId)
    setShowDeleteConnectionModal(true)
    closeManageConnectionPopover()
  }

  // Keep the modal mounted with a spinner on the Remove button while the
  // backend delete is in flight, instead of closing the modal immediately and
  // leaving the user staring at an unchanged repositories tab for 1-2s. The
  // popover takes that long because the backend tears down the connection +
  // its indexes; without feedback users assume the click didn't register.
  const handleDeleteConfirm = async () => {
    if (!connectionToDelete || isDeletingConnection) return
    setIsDeletingConnection(true)
    try {
      const { success, error } = await connectionHook.handleDeleteConnection(connectionToDelete)
      if (success) {
        BetterToast.success({ message: 'Repository Disconnected', duration: 4000 })
        connectionHook.closeRepositoryConnectionState(connectionToDelete)
        navigateToCardList()
      } else {
        // Only claim the AWS session expired when the backend error actually
        // names a credential/SSO failure. Do NOT infer it from anything else —
        // mis-attributing an unknown failure to "run aws sso login" is the exact
        // trap we're fixing. This matches the confirmed natural-expiry error
        // ("Error loading SSO Token…") and botocore's SSO/credential errors.
        // The s3fs-masked "cannot reuse already awaited coroutine" variant is
        // intentionally NOT matched (it doesn't self-identify as a creds error);
        // reliably catching that needs server-side classification (CUR-1450).
        // Everything else keeps the agent-unreachable message (the other common
        // local-dev failure: `agents` isn't running).
        // Word boundaries matter: a bare /sso/ matches the substring in
        // "associated"/"processor"/etc., which would falsely flag unrelated
        // errors as an SSO expiry. \bcredential (no trailing \b) still matches
        // "credentials".
        const isAwsSessionExpired = /\bsso\b|\bcredential/i.test(error ?? '')
        BetterToast.error({
          message: isAwsSessionExpired
            ? 'Your AWS session has expired. Run `aws sso login` to refresh it, then try disconnecting again.'
            : 'Failed to disconnect. The agent service may be unreachable — check that it is running and try again.',
          duration: 6000
        })
      }
    } finally {
      setIsDeletingConnection(false)
      setShowDeleteConnectionModal(false)
      setConnectionToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    if (isDeletingConnection) return
    setShowDeleteConnectionModal(false)
    setConnectionToDelete(null)
  }

  return (
    <>
      <div className='h-[68px] rounded-[16px] w-full bg-[rgba(0,0,0,0.04)] flex items-center justify-between p-4'>
        {/* Left back button */}
        <div className='flex items-center'>
          <button
            onClick={navigateToCardList}
            className='rounded-lg flex items-center justify-center'
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
            >
              <path
                d='M7.825 13L13.425 18.6L12 20L4 12L12 4L13.425 5.4L7.825 11H20V13H7.825Z'
                fill='#1D1B20'
              />
            </svg>
          </button>

          <div
            className={`flex items-center ml-3 flex-1 justify-center ${isLocalConnection ? 'gap-x-[9px]' : 'gap-x-2'}`}
          >
            <div className='w-5 h-5 flex items-center justify-center'>{connection.icon}</div>
            <span className='text-[15px] font-semibold text-[rgba(46,31,54,0.90)] font-mono select-none'>
              {isLocalConnection ? 'Local Repository' : connection.name}
            </span>
            {!isLocalConnection && (
              <Tag variant='success' className='w-[71px] h-4 select-none'>
                CONNECTED
              </Tag>
            )}
          </div>
        </div>

        {/* Right settings button - Only display for non-local connections */}
        {!isLocalConnection && (
          <Popover
            isOpen={manageConnectionPopoverOpen}
            onOpenChange={onManageConnectionPopoverOpenChange}
            placement='bottom-start'
            offset={5}
            classNames={{
              content: 'shadow-popover bg-white w-[235px]',
              trigger: 'aria-expanded:!opacity-100 aria-expanded:!scale-100'
            }}
          >
            <PopoverTrigger>
              <button
                className={cm(
                  'h-9 w-[189px] flex p-[8px_16px] justify-center items-center gap-2 text-base text-[#262626] rounded-[12px] border border-[#D6D6D6] transition-colors',
                  manageConnectionPopoverOpen
                    ? 'bg-[#E2E2E2] !opacity-100 text-[rgba(0,0,0,0.88)]'
                    : 'hover:bg-[#E2E2E2] active:bg-[#E2E2E2]'
                )}
              >
                <NewGearIcon />
                <span className='font-normal select-none'>Manage Connection</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className='!rounded-xl p-2'>
              <ConnectPopoverContent
                conn={connection as TypeConnection}
                isConnected={true}
                isUpdatingToken={connectionHook.isUpdatingToken}
                onConnect={() => {
                  if (!currentWorkspace) {
                    console.warn('Current workspace not available')
                    return
                  }

                  const connType = (connection as TypeConnection).id.toLowerCase()
                  const oauthUrl = `/oauth/${connType}/login?workspace_uuid=${currentWorkspace.uuid}`
                  // Synchronous + preflight-as-beforeOpen so the popup isn't
                  // blocked (see RepositoryConnectionCard / useOAuthPopup).
                  openOAuthPopup(oauthUrl, {
                    beforeOpen: () => preflightOAuthConnect(connType, currentWorkspace.uuid),
                    onSuccess: data => {
                      console.log('OAuth success:', data)
                      onOAuthSuccess(connType)
                    },
                    onError: error => {
                      console.error('OAuth error:', error)
                    }
                  })
                }}
                onDeleteConnection={handleDeleteClick}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
      <ConfirmationModal
        icon={<DeleteWorkspaceIcon className='w-6 h-6' />}
        isOpen={showDeleteConnectionModal}
        onClose={handleDeleteCancel}
        title='Remove all Repositories from Curie'
        content="This action will disconnect all repositories from Curie and deletes all indexed data. Once removed, you'll need to reconnect to use it again."
        buttons={
          <div className='flex gap-[5px] select-none'>
            <Button
              variant='outlined'
              onClick={handleDeleteCancel}
              isDisabled={isDeletingConnection}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeleteConfirm}
              isLoading={isDeletingConnection}
            >
              Remove
            </Button>
          </div>
        }
      />
    </>
  )
}

export function RepositoriesSettingsTab() {
  // URL-based routing
  const [searchParams, setSearchParams] = useSearchParams()

  // Use extracted hooks and contexts
  const runtimeSettingsHook = useRuntimeSettings()
  const connectionHook = useConnectionManagementContext()
  const indexes = useIndexesContext()

  // Indexes use staleTime: Infinity and refresh exclusively via WebSocket
  // events. If a socket event is missed (drop, reconnect gap, OAuth in another
  // tab) the cache stays stale and a successfully indexed repo never appears
  // here even though it's usable in chat. Force a refetch on mount so opening
  // the tab always shows current state. Connection status is also refreshed
  // since it shares the same WS-driven freshness assumption.
  useEffect(() => {
    indexes.refetch()
    connectionHook.refreshConnections()
    // Run once on mount — `indexes`/`connectionHook` identities are unstable
    // and including them would refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // URL-based navigation state
  const currentView = searchParams.get('view') || PageView.CARD_LIST
  const selectedConnectionId = searchParams.get('conn')
  const selectedConnection = useMemo(() => {
    if (!selectedConnectionId) return null
    if (selectedConnectionId === 'local') {
      return {
        id: 'local' as const,
        name: 'Local Repo',
        icon: <NewIconLocalRepository className='w-8 h-8 text-gray-600' />
      } as ConnectionUnion
    }

    return Object.values(Connections).find(conn => conn.id === selectedConnectionId) || null
  }, [selectedConnectionId])
  // Whether the URL-selected connection is actually connected. `local` is
  // always allowed; everything else must be in the live connected set.
  const isSelectedConnected =
    selectedConnection?.id === 'local' ||
    (!!selectedConnection &&
      connectionHook.connectedConnList.some(
        c => c.toLowerCase() === selectedConnection.id.toLowerCase()
      ))

  // A detail URL pointing at a connection that's no longer connected — reached
  // via the back button or a pasted URL after disconnecting — must not render
  // the stale detail view (which has a hardcoded "CONNECTED" badge). Treat it as
  // the card list and clean the URL (effect below). Gated on
  // isConnectionStatusLoading so a fresh load doesn't bounce a valid connection
  // mid-fetch; reactive to connectedConnList so it self-corrects once the
  // post-disconnect status refetch lands.
  const detailIsStale =
    currentView === PageView.INDEX_DETAIL &&
    !!selectedConnection &&
    selectedConnection.id !== 'local' &&
    !connectionHook.isConnectionStatusLoading &&
    !isSelectedConnected

  const shouldShowCardList = currentView === PageView.CARD_LIST || detailIsStale
  const shouldShowDetailView =
    currentView === PageView.INDEX_DETAIL && !!selectedConnection && !detailIsStale
  const shouldShowAuthBranchModal = currentView === PageView.INDEX_MODAL

  // Navigation functions
  const navigateToDetailView = useCallback(
    (connectionId: string) => {
      const params = new URLSearchParams(searchParams)
      params.set('view', PageView.INDEX_DETAIL)
      params.set('conn', connectionId)
      setSearchParams(params)
    },
    [searchParams, setSearchParams]
  )

  const navigateToCardList = useCallback(() => {
    const params = new URLSearchParams(searchParams)
    params.delete('view')
    params.delete('conn')
    setSearchParams(params)
  }, [searchParams, setSearchParams])

  // Bounce a stale detail URL back to the card list (and clean the URL), so a
  // disconnected connection isn't reachable via back button / pasted URL.
  useEffect(() => {
    if (detailIsStale) navigateToCardList()
  }, [detailIsStale, navigateToCardList])

  const navigateToIndexModal = useCallback(
    (connectionId: string) => {
      const params = new URLSearchParams(searchParams)
      params.set('view', PageView.INDEX_MODAL)
      params.set('conn', connectionId)
      setSearchParams(params)
    },
    [searchParams, setSearchParams]
  )

  // Modal states
  const {
    isOpen: authBranchModalVisible,
    onOpen: onAuthBranchModalOpen,
    onClose: onAuthBranchModalClose
  } = useDisclosure()

  const {
    isOpen: customIntegrationVisible,
    onOpen: onCustomIntegrationModalOpen,
    onOpenChange: onCustomIntegrationModalOpenChange
  } = useDisclosure()

  // Handle OAuth success - refresh connections and navigate to index modal.
  // Also refetch the indexes query directly: the mount-time effect on this
  // tab already calls `indexes.refetch()` for the same reason (indexes use
  // staleTime: Infinity and refresh via WebSocket — a re-OAuth that swaps
  // the GitHub App installation produces no WS event for the index list).
  // Without this, the indexed-repos list on the detail view stays stale
  // until a hard refresh.
  const handleOAuthSuccess = useCallback(
    (connectionId: string) => {
      connectionHook.refreshConnections()
      indexes.refetch()
      navigateToIndexModal(connectionId)
    },
    [connectionHook, indexes, navigateToIndexModal]
  )

  const [customIntegrationInitialConnType, setCustomIntegrationInitialConnType] = useState<
    ConnectionType | undefined
  >(undefined)

  const [manageConnectionPopoverOpen, setManageConnectionPopoverOpen] = useState(false)
  const onManageConnectionPopoverOpenChange = useCallback((isOpen: boolean) => {
    setManageConnectionPopoverOpen(isOpen)
  }, [])
  const closeManageConnectionPopover = useCallback(() => {
    setManageConnectionPopoverOpen(false)
  }, [])

  const {
    isOpen: isDrawerOpen,
    onOpen: onDrawerOpen,
    onOpenChange: onDrawerOpenChange
  } = useDisclosure()

  // Handle URL-based conn parameter
  useEffect(() => {
    const connParam = searchParams.get('conn')
    if (connParam) {
      if (shouldShowAuthBranchModal) {
        onAuthBranchModalOpen()
      } else {
        navigateToDetailView(connParam)
        onAuthBranchModalClose()
      }
    }
  }, [
    searchParams,
    onAuthBranchModalOpen,
    navigateToDetailView,
    onAuthBranchModalClose,
    shouldShowAuthBranchModal
  ])

  const handleIndexSelect = (index: FlatIndex) => {
    runtimeSettingsHook.setSelectedIndex(index)
    onDrawerOpen()
  }

  return (
    <div
      className={`settings-tutorial-target ${currentView === PageView.INDEX_DETAIL ? 'h-full flex flex-col' : ''}`}
    >
      {shouldShowCardList && (
        <>
          {/* Grid layout container - Adaptive width, left-aligned */}
          <div className='grid grid-cols-1 xl:grid-cols-2 gap-x-6 gap-y-12 max-w-[824px] min-w-0'>
            {/* Local Repositories card */}
            <LocalRepositoryCard onSelect={() => navigateToDetailView('local')} />

            {/* Other connection service cards */}
            {Object.values(Connections).map(conn => {
              if (conn.hideConn || !connectionHook.connList.includes(conn.id)) return null
              const isConnected = connectionHook.connectedConnList.some(
                connectionId => connectionId.toLowerCase() === conn.id.toLowerCase()
              )

              // Special handling for Self-Hosted GitHub: if not connected, open custom integration modal
              if (conn.id === ConnectionType.SelfHostedGithub && !isConnected) {
                return (
                  <div
                    key={conn.id}
                    className='cursor-pointer w-full min-w-0 max-w-[400px] sm:min-w-[320px]'
                  >
                    <SettingItem
                      variant='card'
                      isConnected={false}
                      className=''
                      title={
                        <>
                          <div className='w-8 h-8 flex items-center justify-center'>
                            {conn.icon}
                          </div>
                          <span className='text-[15px] font-semibold text-[rgba(46,31,54,0.90)] leading-normal font-mono select-none'>
                            {conn.name}
                          </span>
                        </>
                      }
                      onClick={() => {
                        setCustomIntegrationInitialConnType(ConnectionType.SelfHostedGithub)
                        onCustomIntegrationModalOpen()
                      }}
                    >
                      <div className='p-2 rounded-lg pointer-events-none'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                          fill='none'
                        >
                          <g clipPath='url(#clip0_203_17389)'>
                            <path
                              d='M11.3537 8.52035L6.35372 13.5203C6.30727 13.5668 6.25212 13.6037 6.19142 13.6288C6.13072 13.6539 6.06567 13.6669 5.99997 13.6669C5.93428 13.6669 5.86922 13.6539 5.80852 13.6288C5.74783 13.6037 5.69268 13.5668 5.64622 13.5203C5.59977 13.4739 5.56292 13.4187 5.53778 13.358C5.51263 13.2974 5.49969 13.2323 5.49969 13.1666C5.49969 13.1009 5.51263 13.0358 5.53778 12.9752C5.56292 12.9145 5.59977 12.8593 5.64622 12.8128L10.2931 8.1666L5.64622 3.52035C5.5524 3.42653 5.49969 3.29928 5.49969 3.1666C5.49969 3.03392 5.5524 2.90667 5.64622 2.81285C5.74004 2.71903 5.86729 2.66632 5.99997 2.66632C6.13265 2.66632 6.2599 2.71903 6.35372 2.81285L11.3537 7.81285C11.4002 7.85928 11.4371 7.91443 11.4623 7.97513C11.4874 8.03583 11.5004 8.10089 11.5004 8.1666C11.5004 8.23231 11.4874 8.29737 11.4623 8.35807C11.4371 8.41877 11.4002 8.47391 11.3537 8.52035Z'
                              fill='black'
                              fillOpacity='0.88'
                            />
                          </g>
                          <defs>
                            <clipPath id='clip0_203_17389'>
                              <rect
                                width='16'
                                height='16'
                                fill='white'
                                transform='translate(0 0.166626)'
                              />
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                    </SettingItem>
                  </div>
                )
              }

              return (
                <RepositoryConnectionCard
                  key={conn.id}
                  connection={conn}
                  isConnected={isConnected}
                  isUpdatingToken={connectionHook.isUpdatingToken[conn.id] || false}
                  repositoryConnectionState={
                    connectionHook.repositoryConnectionStates[conn.id] || false
                  }
                  onSelect={() => {
                    navigateToDetailView(conn.id)
                  }}
                  onTogglePopover={() => {
                    connectionHook.toggleRepositoryConnectionState(conn.id)
                  }}
                  onOAuthSuccess={handleOAuthSuccess}
                />
              )
            })}

            {/* Self-hosted connection card */}
            <SelfHostedConnectionCard
              onSelect={() => {
                setCustomIntegrationInitialConnType(undefined)
                onCustomIntegrationModalOpen()
              }}
            />
          </div>
        </>
      )}
      {shouldShowDetailView && (
        <>
          {/* Index detail page - Fixed header */}
          <div className='flex-shrink-0 pb-12'>
            <IndexDetailHeader
              connection={selectedConnection}
              navigateToCardList={navigateToCardList}
              manageConnectionPopoverOpen={manageConnectionPopoverOpen}
              onManageConnectionPopoverOpenChange={onManageConnectionPopoverOpenChange}
              closeManageConnectionPopover={closeManageConnectionPopover}
              connectionHook={connectionHook}
              onOAuthSuccess={handleOAuthSuccess}
            />
          </div>

          {/* Content area */}
          <div className='flex-1'>
            {/* Display different content based on connection type */}
            {'id' in selectedConnection && selectedConnection.id === 'local' ? (
              <LocalRepositorySection />
            ) : (
              <ConnectedReposTable
                selectedConnection={selectedConnection}
                onIndexSelect={handleIndexSelect}
              />
            )}
          </div>
        </>
      )}
      <Modal
        isOpen={authBranchModalVisible}
        onClose={() => navigateToDetailView(selectedConnection?.id as string)}
        className={cm(
          bridge.isInPlugin()
            ? 'max-w-[calc(100vw-24px)] w-full mb-4 h-[calc(100vh-80px)] overflow-y-hidden'
            : 'min-w-[720px] max-w-[720px] mb-[160px] h-[calc(100vh-160px-120px)] overflow-y-hidden'
        )}
        overlayClassName='z-[9999] overflow-hidden'
        hideCloseButton={true}
        disableOutsideClick={true}
      >
        <ModalBody className='p-0 h-full flex flex-col'>
          <div className='pl-6 w-full h-16 shrink-0 flex justify-between items-center border-b border-b-[#EAEAEA]'>
            <div className='text-base font-bold text-black leading-[18px] select-none'>
              Select Branches
            </div>
            <ModalCloseButton
              onClick={() => {
                navigateToDetailView(selectedConnection?.id as string)
              }}
            />
          </div>
          <AuthorizeBranchForm
            modalOpenChange={() => navigateToDetailView(selectedConnection?.id as string)}
            connType={selectedConnection?.id as ConnectionType}
          />
        </ModalBody>
      </Modal>

      <Modal
        isOpen={customIntegrationVisible}
        onClose={() => {
          onCustomIntegrationModalOpenChange()
        }}
        className={cm(
          bridge.isInPlugin()
            ? 'max-w-[calc(100vw-24px)] w-full h-[calc(100vh-80px)] overflow-y-hidden'
            : 'min-w-[600px] max-w-[600px] h-[calc(100vh-160px-120px)] overflow-y-hidden'
        )}
        overlayClassName='z-[9999]'
        hideCloseButton={true}
      >
        <ModalBody className='p-0'>
          <div className='pl-6 w-full h-16 flex justify-between items-center border-b border-b-[#EAEAEA]'>
            <div className='text-base font-bold text-black leading-[18px] select-none'>
              Custom Integration
            </div>
            <ModalCloseButton
              onClick={() => {
                onCustomIntegrationModalOpenChange()
              }}
            />
          </div>
          <CustomIntegrationForm
            onCancel={() => {
              onCustomIntegrationModalOpenChange()
            }}
            onOAuthSuccess={handleOAuthSuccess}
            connectedConnList={connectionHook.connectedConnList}
            initialConnType={customIntegrationInitialConnType}
          />
        </ModalBody>
      </Modal>

      {/* Runtime Settings Drawer */}
      <RuntimeSettingsDrawer
        isOpen={isDrawerOpen}
        onOpenChange={onDrawerOpenChange}
        runtimeSettings={runtimeSettingsHook}
      />
    </div>
  )
}
